import { Component, inject } from "@angular/core";
import { ICellRendererParams, IRowNode } from "ag-grid-community";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { DatePipe, DecimalPipe } from "@angular/common";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { isGroceryTransaction } from "@bitwarden/web-vault/app/services/store/data-plugin/transaction-plugin.utils";

export interface PastGroupHeaderParams extends ICellRendererParams {
  /**
   * Set by PastTransactionTable. True while the list intro is rolling this group closed: the grid still
   * reports it as expanded until its rows are dropped, but the chevron should already be turning.
   */
  isIntroCollapsing?: (groupKey: string) => boolean;
  /** True while the list intro hasn't got to this month yet: the header stays hidden, then fades in. */
  isIntroHeaderPending?: (groupKey: string) => boolean;
}

@Component({
  selector: "app-past-group-header",
  templateUrl: "./past-group-header.component.html",
  providers: [DecimalPipe],
  host: {
    // No glass-panel: child bg (tw-bg-dt-surface-card) is opaque, making backdrop-filter inert.
    class:
      "tw-mx-2 tw-my-1 tw-rounded-2xl tw-overflow-hidden tw-shadow-elevation-card tw-flex tw-w-full tw-h-full",
    "[class.past-group-header--pending]": "isIntroPending",
  },
})
export class PastGroupHeaderComponent implements ICellRendererAngularComp {
  private static readonly DAY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
  private static readonly WEEK_REGEX = /^(\d{4})-W(\d{2})$/;
  private static readonly MONTH_REGEX = /^(\d{4})-(\d{2})$/;
  private static readonly QUARTER_REGEX = /^(\d{4})-Q(\d)$/;
  private static readonly YEAR_REGEX = /^(\d{4})$/;

  protected groupBalance: number = 0;
  protected groupCurrency: string = "";
  protected hasGroceryTransaction: boolean = false;

  params!: PastGroupHeaderParams;
  private lastLeafChildren: IRowNode[] | undefined;

  datePipe = inject(DatePipe);
  decimalPipe = inject(DecimalPipe);
  private pluginConfigService = inject(PluginConfigService);

  agInit(params: PastGroupHeaderParams): void {
    this.params = params;
    this.fetchGroupBalance();
    this.computeGroceryFlag();
  }

  refresh(params: PastGroupHeaderParams): boolean {
    this.params = params;
    this.fetchGroupBalance();
    this.computeGroceryFlag();
    return true;
  }

  get isExpanded(): boolean {
    const node = this.params?.node;
    return !!node?.expanded && !(node.key != null && this.params.isIntroCollapsing?.(node.key));
  }

  get isIntroPending(): boolean {
    const key = this.params?.node?.key;
    return key != null && !!this.params.isIntroHeaderPending?.(key);
  }

  get isExpandable(): boolean {
    const node = this.params?.node;
    return (
      !!node && !!node.group && !!node.childrenAfterGroup && node.childrenAfterGroup.length > 0
    );
  }

  toggleExpand(event: MouseEvent): void {
    event.stopPropagation();
    if (this.params?.node) {
      this.params.node.setExpanded(!this.params.node.expanded);
    }
  }

  get groupLabel(): string {
    const value = this.params?.value;
    if (!value) {
      return "";
    }
    // Handle day: YYYY-MM-DD
    const dayMatch = PastGroupHeaderComponent.DAY_REGEX.exec(value);
    if (dayMatch) {
      // Use DatePipe for formatting
      return this.datePipe.transform(value, "MMM d, y") || value;
    }
    // Handle week: YYYY-Www
    const weekMatch = PastGroupHeaderComponent.WEEK_REGEX.exec(value);
    if (weekMatch) {
      const year = weekMatch[1];
      const week = weekMatch[2];
      return `Week ${parseInt(week, 10)}, ${year}`;
    }
    // Handle month: YYYY-MM
    const monthMatch = PastGroupHeaderComponent.MONTH_REGEX.exec(value);
    if (monthMatch) {
      // Use DatePipe for formatting first day of month
      return this.datePipe.transform(value + "-01", "MMM y") || value;
    }
    // Handle quarter: YYYY-Qn
    const quarterMatch = PastGroupHeaderComponent.QUARTER_REGEX.exec(value);
    if (quarterMatch) {
      const year = quarterMatch[1];
      const quarter = quarterMatch[2];
      return `Q${quarter} ${year}`;
    }
    // Handle year: YYYY
    if (PastGroupHeaderComponent.YEAR_REGEX.test(value)) {
      return value;
    }
    // fallback
    return value;
  }

  // Count only real transactions — revaluations (c_isRevaluation) are synthetic and must not be
  // counted. ag-grid's own allChildrenCount includes them, so we tally the leaf rows ourselves.
  // A group with only revals yields 0, and the template hides the label entirely in that case.
  get count(): number {
    const leaves = this.params?.node?.allLeafChildren;
    if (!leaves) {
      return 0;
    }
    return leaves.filter((leaf) => leaf?.data?.transactionView?.c_isRevaluation !== true).length;
  }

  fetchGroupBalance(): void {
    const nodesInGroup = this.params?.node?.allLeafChildren;
    if (nodesInGroup && nodesInGroup.length > 0) {
      this.groupBalance = nodesInGroup[0]?.data?.balance ?? 0;
      this.groupCurrency = nodesInGroup[0]?.data?.symbol ?? "";
    }
  }

  get groupBalanceClass(): string {
    if (this.groupBalance > 0) {
      return "tw-text-dt-text-in-primary";
    }
    if (this.groupBalance < 0) {
      return "tw-text-dt-text-out-primary";
    }
    return "tw-text-dt-text-secondary";
  }

  get formattedGroupBalance(): string {
    return this.decimalPipe.transform(this.groupBalance, "1.2-2") || "0.00";
  }

  private computeGroceryFlag(): void {
    const children = this.params?.node?.allLeafChildren;
    if (children === this.lastLeafChildren) {
      return;
    }
    this.lastLeafChildren = children;

    if (!this.pluginConfigService.isTransactionPluginEnabled()) {
      this.hasGroceryTransaction = false;
      return;
    }
    this.hasGroceryTransaction = !!children?.some((node) =>
      isGroceryTransaction(node?.data?.description),
    );
  }
}
